import {
	faCheck,
	faCog,
	faCompress,
	faExclamationTriangle,
	faExpand,
	faFileExport,
	faFilter,
	faFilterSlash,
	faPencil,
	faRefresh,
	faTimes,
} from '@fortawesome/pro-solid-svg-icons';
import type { FilterState } from '@supply-mover-2-0/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
// Sibling-package imports — relative since this file lives in libs/web-ui.
import { Icon } from '../components/base/Icon';
import { Text } from '../components/base/Text';
import { Card } from '../components/display/Card';
import { LoadingSpinner } from '../components/display/LoadingSpinner';
import { Input } from '../components/forms/Input';
import { Modal } from '../components/overlays/Modal';
import { WidgetExportProvider } from '../contexts/WidgetExportContext';
import { useThemeColors } from '../providers/ThemeProvider';
import { useTranslation } from '../providers/TranslationProvider';
import { cn } from '../utils/cn';
import type { WidgetFilterConfig } from './types/filters.types';
import type { WidgetProps } from './types/widget.types';

interface WidgetContainerProps extends Omit<WidgetProps, 'data'> {
	/** Widget data — undefined until the data store first hydrates this widget */
	data: WidgetProps['data'] | undefined;
	children: React.ReactNode;
	editMode?: boolean;
	className?: string;
	/** Dashboard-level filters */
	dashboardFilters?: FilterState;
	/** Available filters for this widget's report */
	availableFilters?: string[];
	/** Custom filter configurations for this widget's report */
	customFilters?: Record<string, any>;
	/** Custom date range options for this widget's report */
	dateRangeOptions?: Array<{ value: string; label: string }> | null;
	/** Custom date range calculator for this widget's report */
	dateRangeCalculator?: ((value: string) => any) | null;
	/** Callback when widget filters change */
	onWidgetFiltersChange?: (
		widgetId: string,
		overrides: FilterState,
		config: WidgetFilterConfig,
	) => void;
	/** Callback when widget title changes */
	onTitleChange?: (newTitle: string) => void;
	/** Callback when filters change - for WidgetRenderer */
	onFiltersChange?: (filters: FilterState) => void;

	/**
	 * Filter-manager slot. CRM injects its `<WidgetFilterManager>`
	 * (report-aware filter UI). Storefront passes nothing — its
	 * widgets don't have user-facing filters. Called only when the
	 * filter button is clicked.
	 */
	renderFilterManager?: (ctx: {
		widget: WidgetProps['config'];
		dashboardFilters: FilterState;
		availableFilters: string[];
		customFilters: Record<string, any>;
		dateRangeOptions: Array<{ value: string; label: string }> | null;
		dateRangeCalculator: ((value: string) => any) | null;
		widgetOverrides: Record<string, any[]>;
		categoryCounts: Record<string, number>;
		isVisible: boolean;
		onVisibilityChange: (v: boolean) => void;
		onFiltersChange: (
			overrides: FilterState,
			config: WidgetFilterConfig,
		) => void;
	}) => React.ReactNode;

	/**
	 * Fullscreen renderer slot — what to show when the user expands
	 * a widget. CRM passes a renderer that uses the same widget body
	 * as the grid (via `<WidgetRenderer>`); storefront can pass a
	 * different layout if it wants.
	 */
	renderFullscreenWidget?: (
		widget: WidgetProps['config'],
		data: WidgetProps['data'] | undefined,
		ctx: {
			dashboardFilters?: FilterState;
			onFiltersChange?: (filters: FilterState) => void;
		},
	) => React.ReactNode;

	/**
	 * Strip the widget chrome (title bar, refresh / settings / filter
	 * / menu icons, drag handle) and render just the children. The
	 * customer-facing storefront uses this — end users can't refresh,
	 * filter, expand, or rearrange widgets, so the chrome is dead UI
	 * for them. Defaults to false so the CRM dashboard stays
	 * unchanged.
	 */
	hideChrome?: boolean;
}

function WidgetContainerBase({
	config,
	data,
	onRefresh,
	onSettings,
	onRemove,
	onTitleChange,
	editMode = false,
	children,
	className,
	dashboardFilters = {},
	availableFilters = [],
	customFilters = {},
	dateRangeOptions = null,
	dateRangeCalculator = null,
	onWidgetFiltersChange,
	renderFilterManager,
	renderFullscreenWidget,
	hideChrome = false,
}: WidgetContainerProps) {
	// Storefront opts in to a chrome-free render: no title bar, no
	// refresh / settings / filter / menu / fullscreen icons, no drag
	// handle, no card frame. The customer can't interact with these
	// controls (read-only design served by an admin), so dropping
	// them removes dead UI and stops the chrome from eating into the
	// widget's saved height. The widget body renders directly so each
	// promo widget controls its own visual treatment.
	if (hideChrome) {
		return <>{children}</>;
	}
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [showWidgetFilters, setShowWidgetFilters] = useState(false);
	const [showFullscreen, setShowFullscreen] = useState(false);
	const titleInputRef = useRef<HTMLInputElement>(null);
	// Ref to the widget body's scrollable wrapper. Used by the
	// wheel-chaining effect below so a wheel event over the widget can
	// (a) bubble to the dashboard's scroll container when the widget
	// can't scroll further in that direction and (b) be redirected to
	// the dashboard when the user is still mid-scrolling the dashboard
	// (cursor just happened to drift over the widget).
	const widgetBodyRef = useRef<HTMLDivElement>(null);

	// Inline title editing state
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [editTitleValue, setEditTitleValue] = useState('');

	// State for category counts (for radio filters)
	const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(
		{},
	);

	// Export function registered by ReportCsvExportButton via WidgetExportContext
	const exportFnRef = useRef<(() => void) | null>(null);
	const [hasExport, setHasExport] = useState(false);
	const handleExportRegistered = useCallback(
		(exportFn: (() => void) | null) => {
			exportFnRef.current = exportFn;
			setHasExport(exportFn != null);
		},
		[],
	);

	// Get current widget filter overrides and inheritance mode
	const widgetOverrides = config.filterConfig?.overrides || {};
	const inheritanceMode = config.filterConfig?.inheritanceMode || 'inherit';

	// Count only filters that have actual values (not empty arrays or null/undefined)
	const activeFilterCount = Object.entries(widgetOverrides).filter(
		([key, value]) => {
			// Check if the filter has a meaningful value
			if (value == null) return false;
			if (Array.isArray(value)) {
				// For arrays, check if they have items and if those items are meaningful
				if (value.length === 0) return false;
				// Check if array items are objects (like {id, label}) - still count them
				return true;
			}
			if (
				typeof value === 'object' &&
				'startDate' in value &&
				'endDate' in value
			)
				return true; // Date range
			if (typeof value === 'string' || typeof value === 'number') {
				// Don't count empty strings or default 'all' value
				return value !== '' && value !== 'all';
			}
			if (typeof value === 'object') return Object.keys(value).length > 0;
			return false;
		},
	).length;

	// Check if widget has active filters
	const hasActiveFilters = activeFilterCount > 0;

	const handleWidgetFiltersChange = (
		overrides: FilterState,
		filterConfig: WidgetFilterConfig,
	) => {
		if (onWidgetFiltersChange) {
			// Encode the filterConfig into the filters object to pass through the 2-parameter callback
			// This is a workaround since the callback signature only accepts 2 parameters
			// The _config property will be extracted in DashboardMain
			const filtersWithConfig: any = {
				...overrides,
				_config: filterConfig,
			};
			onWidgetFiltersChange(config.id, filtersWithConfig, filterConfig);
		}
	};

	const handleTitleEditStart = () => {
		setEditTitleValue(config.title || '');
		setIsEditingTitle(true);
	};

	const handleTitleEditSave = () => {
		const trimmedTitle = editTitleValue.trim();
		if (trimmedTitle && trimmedTitle !== config.title && onTitleChange) {
			onTitleChange(trimmedTitle);
		}
		setIsEditingTitle(false);
	};

	const handleTitleEditCancel = () => {
		setIsEditingTitle(false);
		setEditTitleValue('');
	};

	const handleTitleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			handleTitleEditSave();
		} else if (event.key === 'Escape') {
			handleTitleEditCancel();
		}
	};

	// Focus input when editing starts
	useEffect(() => {
		if (isEditingTitle && titleInputRef.current) {
			titleInputRef.current.focus();
			titleInputRef.current.select();
		}
	}, [isEditingTitle]);

	// Scroll chaining + momentum debounce.
	//
	// Without this, two annoyances bite the user on a tall dashboard:
	//   1. When the widget can't scroll in the wheel direction (either
	//      not scrollable, or already at the top/bottom bound), the wheel
	//      should propagate to the dashboard. Chrome's default chain
	//      doesn't always do this reliably between nested overflow-auto
	//      containers.
	//   2. While the user is mid-scrolling the dashboard, if the cursor
	//      drifts over a widget, the widget shouldn't immediately steal
	//      the scroll — it should keep going on the dashboard until the
	//      user pauses, then resume taking over.
	//
	// Implementation: on each wheel event we (a) find the nearest
	// ACTUALLY-SCROLLABLE ancestor (lazy because the dashboard grows
	// dynamically as widgets load), and (b) decide whether to redirect.
	// If no scrollable ancestor exists yet, we let native bubbling do
	// whatever it does — `preventDefault`-ing with nowhere to redirect
	// would just freeze the page.
	useEffect(() => {
		const widgetScroller = widgetBodyRef.current;
		if (widgetScroller == null) {
			return;
		}

		const SCROLL_DEBOUNCE_MS = 250;
		let lastParentScrollTime = 0;
		let attachedScrollParent: HTMLElement | null = null;

		const onParentScroll = () => {
			lastParentScrollTime = Date.now();
		};

		const findScrollableParent = (): HTMLElement | null => {
			let p: HTMLElement | null = widgetScroller.parentElement;
			while (p != null) {
				const style = getComputedStyle(p);
				const overflowY = style.overflowY;
				const couldScroll = overflowY === 'auto' || overflowY === 'scroll';
				// `couldScroll` alone isn't enough — many wrappers carry
				// `overflow-auto` defensively without ever overflowing. Only
				// treat as a chain target if it ACTUALLY has scroll room.
				if (couldScroll && p.scrollHeight > p.clientHeight + 1) {
					return p;
				}
				p = p.parentElement;
			}
			return null;
		};

		const ensureScrollListener = (parent: HTMLElement | null) => {
			if (parent === attachedScrollParent) {
				return;
			}
			if (attachedScrollParent != null) {
				attachedScrollParent.removeEventListener('scroll', onParentScroll);
			}
			attachedScrollParent = parent;
			if (parent != null) {
				parent.addEventListener('scroll', onParentScroll, { passive: true });
			}
		};

		const canElementScrollInDirection = (
			el: HTMLElement,
			deltaY: number,
		): boolean => {
			if (el.scrollHeight <= el.clientHeight + 1) {
				return false;
			}
			const atTop = el.scrollTop <= 0;
			const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
			return deltaY > 0 ? !atBottom : !atTop;
		};

		const onWheel = (e: WheelEvent) => {
			// Only handle vertical wheel. Horizontal and modifier-wheel
			// (zoom etc.) fall through.
			if (e.deltaY === 0 || e.ctrlKey || e.metaKey) {
				return;
			}

			// If there's an inner scrollable element between the wheel
			// target and the widget body that can still scroll in this
			// direction, let it handle natively. Without this check, a
			// widget like the goals table (whose inner Table has its own
			// overflow-auto wrapper) would never be able to scroll —
			// widgetScroller looks empty from outside, so we'd redirect
			// every wheel to the dashboard.
			let inner: HTMLElement | null = e.target as HTMLElement | null;
			while (inner != null && inner !== widgetScroller) {
				const innerStyle = getComputedStyle(inner);
				const innerOverflowY = innerStyle.overflowY;
				if (
					(innerOverflowY === 'auto' || innerOverflowY === 'scroll') &&
					canElementScrollInDirection(inner, e.deltaY)
				) {
					return;
				}
				inner = inner.parentElement;
			}

			const parent = findScrollableParent();
			ensureScrollListener(parent);

			// No scrollable parent → don't preventDefault; native bubbling
			// handles whatever it can.
			if (parent == null) {
				return;
			}

			const canScrollInDirection = canElementScrollInDirection(
				widgetScroller,
				e.deltaY,
			);

			const parentRecentlyScrolled =
				Date.now() - lastParentScrollTime < SCROLL_DEBOUNCE_MS;

			if (parentRecentlyScrolled || !canScrollInDirection) {
				e.preventDefault();
				// `deltaMode` 1 = lines (~16px each), 2 = pages (one widget
				// height) — normalize to pixels so trackpads and old mice
				// both feel proportional.
				const delta =
					e.deltaMode === 1
						? e.deltaY * 16
						: e.deltaMode === 2
							? e.deltaY * widgetScroller.clientHeight
							: e.deltaY;
				parent.scrollTop += delta;
			}
		};
		widgetScroller.addEventListener('wheel', onWheel, { passive: false });

		return () => {
			widgetScroller.removeEventListener('wheel', onWheel);
			if (attachedScrollParent != null) {
				attachedScrollParent.removeEventListener('scroll', onParentScroll);
			}
		};
	}, []);

	return (
		<Card
			className={cn(
				'w-full h-full p-0 overflow-hidden rounded-xl shadow-sm flex flex-col',
				colors.classes.bgPrimary,
				className,
			)}
		>
			{/* Widget Header */}
			<div
				className={cn(
					'flex justify-between items-center px-4 py-3 rounded-t-xl',
					colors.classes.bgSecondary,
				)}
			>
				<div className="flex-1 flex items-center">
					{isEditingTitle ? (
						<div className="flex-1 flex items-center gap-2">
							<Input
								ref={titleInputRef}
								type="text"
								value={editTitleValue}
								onChange={(e) => setEditTitleValue(e.target.value)}
								onBlur={handleTitleEditSave}
								onKeyDown={handleTitleKeyPress}
								className="flex-1 text-base font-bold tracking-tight"
							/>
							{/*
							 * `onMouseDown` runs before the input's blur, so
							 * preventDefault here stops the cancel/save click
							 * from first triggering the auto-save blur and
							 * then the click's own action — without it,
							 * Cancel would race against blur=Save and the
							 * user's edits could leak through.
							 */}
							<button
								onMouseDown={(e) => e.preventDefault()}
								onClick={handleTitleEditSave}
								title={t('Save')}
								className={cn(
									'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
									colors.classes.bgInfo,
									colors.classes.bgInfoHover,
									'transition-colors',
								)}
							>
								<Icon
									icon={faCheck}
									size={14}
									className={colors.classes.statusInfo}
								/>
							</button>
							<button
								onMouseDown={(e) => e.preventDefault()}
								onClick={handleTitleEditCancel}
								title={t('Cancel')}
								className={cn(
									'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
									colors.classes.bgDanger,
									colors.classes.bgDangerHover,
									'transition-colors',
								)}
							>
								<Icon
									icon={faTimes}
									size={14}
									className={colors.classes.statusError}
								/>
							</button>
						</div>
					) : (
						<div className="flex items-center flex-1">
							<Text
								className={cn(
									'text-base font-bold tracking-tight',
									colors.classes.textPrimary,
								)}
							>
								{t(config.title)}
							</Text>
							{editMode && onTitleChange && (
								<button
									onClick={handleTitleEditStart}
									className={cn(
										'ml-2 w-8 h-8 rounded-lg flex items-center justify-center',
										colors.classes.bgInfo,
										colors.classes.bgInfoHover,
										'transition-colors',
									)}
								>
									<Icon
										icon={faPencil}
										size={16}
										className={colors.classes.statusInfo}
									/>
								</button>
							)}
						</div>
					)}
				</div>

				<div className="flex gap-3">
					{!editMode && data && !data.loading && !data.error && (
						<button
							onClick={onRefresh}
							className={cn(
								'w-8 h-8 rounded-lg flex items-center justify-center',
								colors.classes.bgTertiary,
								colors.classes.bgPrimaryHover,
								'transition-colors',
							)}
						>
							<Icon
								icon={faRefresh}
								size={16}
								className={colors.classes.textSecondary}
							/>
						</button>
					)}

					{onSettings && (
						<button
							onClick={onSettings}
							className={cn(
								'w-8 h-8 rounded-lg flex items-center justify-center',
								colors.classes.bgTertiary,
								colors.classes.bgPrimaryHover,
								'transition-colors',
							)}
							title={t('Widget settings')}
						>
							<Icon
								icon={faCog}
								size={16}
								className={colors.classes.textSecondary}
							/>
						</button>
					)}

					{!editMode && (
						<div className="relative">
							<button
								onClick={() => setShowWidgetFilters(true)}
								className={cn(
									'w-8 h-8 rounded-lg flex items-center justify-center transition-colors',
									hasActiveFilters
										? cn(
												colors.classes.bgInfo,
												'border',
												colors.classes.borderInfo,
												colors.classes.bgInfoHover,
											)
										: cn(
												colors.classes.bgTertiary,
												colors.classes.bgPrimaryHover,
											),
								)}
								style={
									hasActiveFilters
										? {
												backgroundColor: colors.values.primary,
												borderColor: colors.values.primary,
											}
										: undefined
								}
							>
								<Icon
									icon={hasActiveFilters ? faFilter : faFilterSlash}
									size={16}
									className={
										hasActiveFilters
											? colors.classes.textOnPrimary
											: colors.classes.textSecondary
									}
									color={
										hasActiveFilters ? colors.values.textInverse : undefined
									}
								/>
							</button>
							{hasActiveFilters && (
								<div
									className={cn(
										'absolute -top-1 -right-1 rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2',
										'bg-red-500 border-white dark:border-gray-800',
									)}
								>
									<Text className={cn('text-[10px] font-bold text-white')}>
										{activeFilterCount}
									</Text>
								</div>
							)}
						</div>
					)}

					{!editMode && hasExport && (
						<button
							onClick={() => exportFnRef.current?.()}
							title={t('Export CSV')}
							className={cn(
								'w-8 h-8 rounded-lg flex items-center justify-center',
								colors.classes.bgTertiary,
								colors.classes.bgPrimaryHover,
								'transition-colors',
							)}
						>
							<Icon
								icon={faFileExport}
								size={16}
								className={colors.classes.textSecondary}
							/>
						</button>
					)}

					{!editMode && (
						<button
							onClick={() => setShowFullscreen(true)}
							title={t('Full Screen')}
							className={cn(
								'w-8 h-8 rounded-lg flex items-center justify-center',
								colors.classes.bgTertiary,
								colors.classes.bgPrimaryHover,
								'transition-colors',
							)}
						>
							<Icon
								icon={faExpand}
								size={16}
								className={colors.classes.textSecondary}
							/>
						</button>
					)}

					{editMode && onRemove && !isEditingTitle && (
						<button
							onClick={onRemove}
							className={cn(
								'w-8 h-8 rounded-lg flex items-center justify-center',
								colors.classes.bgDanger,
								colors.classes.bgDangerHover,
								'transition-colors',
							)}
						>
							<Icon
								icon={faTimes}
								size={16}
								className={colors.classes.statusError}
							/>
						</button>
					)}
				</div>
			</div>

			{/* Widget Content */}
			<div className="p-4 flex-1 min-h-0 flex flex-col">
				{data?.loading ? (
					<div className="flex justify-center items-center min-h-[120px] w-full">
						<div
							className={cn(
								'p-5 rounded-xl text-center',
								colors.classes.bgSecondary,
							)}
						>
							<LoadingSpinner size="lg" className="mx-auto mb-3" />
							<Text
								className={cn(
									'text-sm font-medium',
									colors.classes.textSecondary,
								)}
							>
								{t('Loading...')}
							</Text>
						</div>
					</div>
				) : data?.error ? (
					<div className="flex justify-center items-center min-h-[120px]">
						<div
							className={cn(
								'p-6 rounded-xl border text-center',
								colors.classes.bgDanger,
								colors.classes.borderError,
							)}
						>
							<div
								className={cn(
									'w-12 h-12 rounded-full flex items-center justify-center mb-4 mx-auto',
									colors.classes.bgDanger,
								)}
							>
								<Icon
									icon={faExclamationTriangle}
									size={24}
									className={colors.classes.statusError}
								/>
							</div>
							<Text
								className={cn(
									'text-sm font-semibold mb-1',
									colors.classes.statusError,
								)}
							>
								{t('Error Loading Data')}
							</Text>
							<Text className={cn('text-xs mb-4', colors.classes.statusError)}>
								{t(data?.error)}
							</Text>
							<button
								onClick={onRefresh}
								className={cn(
									'px-5 py-2.5 text-white',
									colors.classes.primaryButton,
									'rounded-lg font-medium text-sm',
									'flex items-center gap-2 mx-auto',
									'transition-colors',
								)}
							>
								<Icon icon={faRefresh} size={14} />
								{t('Retry')}
							</button>
						</div>
					</div>
				) : (
					<div className="w-full flex-1 min-h-0 relative">
						{/* PD-14198: body must NOT scroll — overflowing report content (a
					    Table) brings its own scroll container and the wheel handler
					    above already defers to it, so overflow-auto here produced a
					    SECOND scrollbar (nested double-scroll + trailing blank space).
					    overflow-hidden leaves the inner Table as the single scroller;
					    non-overflowing widgets (charts/KPIs) are unaffected. */}
						<div
							ref={widgetBodyRef}
							className="absolute inset-0 overflow-hidden"
						>
							<WidgetExportProvider onExportRegistered={handleExportRegistered}>
								{React.isValidElement(children)
									? React.cloneElement(children as React.ReactElement<any>, {
											onCategoryCountsUpdate: setCategoryCounts,
										})
									: children}
							</WidgetExportProvider>
						</div>
					</div>
				)}
			</div>

			{/* Widget Filter Manager — consumer slot. CRM passes a
			    `<WidgetFilterManager>` (report-aware filter UI);
			    storefront passes nothing. */}
			{showWidgetFilters &&
				renderFilterManager?.({
					widget: config,
					dashboardFilters,
					availableFilters,
					customFilters,
					dateRangeOptions,
					dateRangeCalculator,
					widgetOverrides,
					categoryCounts,
					isVisible: showWidgetFilters,
					onVisibilityChange: setShowWidgetFilters,
					onFiltersChange: handleWidgetFiltersChange,
				})}

			{/* Full Screen Modal */}
			<Modal
				isOpen={showFullscreen}
				onClose={() => setShowFullscreen(false)}
				size="full"
				showCloseButton={false}
			>
				<div className="h-[calc(100vh-200px)] flex flex-col -mx-6 -my-4">
					{/* Custom Header with title and action buttons */}
					<div
						className={cn(
							'flex justify-between items-center px-6 py-4 border-b',
							colors.classes.borderDefault,
						)}
					>
						<Text
							className={cn('text-xl font-bold', colors.classes.textPrimary)}
						>
							{t(config.title)}
						</Text>
						<div className="flex gap-3">
							{data && !data.loading && !data.error && (
								<button
									onClick={onRefresh}
									className={cn(
										'w-10 h-10 rounded-lg flex items-center justify-center',
										colors.classes.bgTertiary,
										colors.classes.bgPrimaryHover,
										'transition-colors',
									)}
								>
									<Icon
										icon={faRefresh}
										size={18}
										className={colors.classes.textSecondary}
									/>
								</button>
							)}
							<button
								onClick={() => setShowFullscreen(false)}
								className={cn(
									'w-10 h-10 rounded-lg flex items-center justify-center',
									colors.classes.bgTertiary,
									colors.classes.bgPrimaryHover,
									'transition-colors',
								)}
							>
								<Icon
									icon={faCompress}
									size={18}
									className={colors.classes.textSecondary}
								/>
							</button>
						</div>
					</div>

					{/* Modal Content - no padding */}
					<div className="flex-1 overflow-auto">
						{data?.loading ? (
							<div className="flex justify-center items-center min-h-[400px]">
								<div
									className={cn(
										'p-8 rounded-xl text-center',
										colors.classes.bgSecondary,
									)}
								>
									<LoadingSpinner size="xl" className="mx-auto mb-4" />
									<Text
										className={cn(
											'text-lg font-medium',
											colors.classes.textSecondary,
										)}
									>
										{t('Loading...')}
									</Text>
								</div>
							</div>
						) : data?.error ? (
							<div className="flex justify-center items-center min-h-[400px]">
								<div
									className={cn(
										'p-8 rounded-xl border text-center',
										colors.classes.bgDanger,
										colors.classes.borderError,
									)}
								>
									<div
										className={cn(
											'w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto',
											colors.classes.bgDanger,
										)}
									>
										<Icon
											icon={faExclamationTriangle}
											size={32}
											className={colors.classes.statusError}
										/>
									</div>
									<Text
										className={cn(
											'text-lg font-semibold mb-2',
											colors.classes.statusError,
										)}
									>
										{t('Error Loading Data')}
									</Text>
									<Text
										className={cn('text-sm mb-6', colors.classes.statusError)}
									>
										{t(data?.error)}
									</Text>
									<button
										onClick={onRefresh}
										className={cn(
											'px-6 py-3 text-white',
											colors.classes.primaryButton,
											'rounded-lg font-medium',
											'flex items-center gap-2 mx-auto',
											'transition-colors',
										)}
									>
										<Icon icon={faRefresh} size={16} />
										{t('Retry')}
									</button>
								</div>
							</div>
						) : (
							<div className="w-full h-full">
								{renderFullscreenWidget?.(config, data, {
									dashboardFilters,
									onFiltersChange: handleWidgetFiltersChange
										? (filters) =>
												handleWidgetFiltersChange(
													filters,
													config.filterConfig ?? { overrides: {} },
												)
										: undefined,
								})}
							</div>
						)}
					</div>
				</div>
			</Modal>
		</Card>
	);
}

// Deep equality check for objects
const deepEqual = (obj1: any, obj2: any): boolean => {
	if (obj1 === obj2) return true;
	if (obj1 == null || obj2 == null) return false;
	if (typeof obj1 !== 'object' || typeof obj2 !== 'object')
		return obj1 === obj2;

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) return false;

	for (const key of keys1) {
		if (!keys2.includes(key)) return false;
		if (!deepEqual(obj1[key], obj2[key])) return false;
	}

	return true;
};

// Memoized WidgetContainer to prevent unnecessary re-renders when only position changes
export const WidgetContainer = React.memo(
	WidgetContainerBase,
	(prevProps, nextProps) => {
		// Check what changed
		const configChanged =
			prevProps.config.id !== nextProps.config.id ||
			prevProps.config.type !== nextProps.config.type ||
			prevProps.config.title !== nextProps.config.title ||
			!deepEqual(prevProps.config.settings, nextProps.config.settings) ||
			!deepEqual(prevProps.config.filterConfig, nextProps.config.filterConfig);

		const dataChanged = prevProps.data !== nextProps.data;
		const filtersChanged = !deepEqual(
			prevProps.dashboardFilters,
			nextProps.dashboardFilters,
		);
		const editModeChanged = prevProps.editMode !== nextProps.editMode;

		// Check callback changes
		const onRefreshChanged = prevProps.onRefresh !== nextProps.onRefresh;
		const onRemoveChanged = prevProps.onRemove !== nextProps.onRemove;
		const onTitleChangeChanged =
			prevProps.onTitleChange !== nextProps.onTitleChange;
		const onWidgetFiltersChangeChanged =
			prevProps.onWidgetFiltersChange !== nextProps.onWidgetFiltersChange;

		// Check if children prop changed (even though we removed it from decision)
		const childrenSame = prevProps.children === nextProps.children;

		// If any important prop changed, re-render
		if (configChanged || dataChanged || filtersChanged || editModeChanged) {
			return false; // Props changed, should re-render
		}

		return true;
	},
);
